/**
 * Address validation utilities for VEPA and other address-related checks
 */

export function isTBDAddress(address: string | null | undefined): boolean {
  return !address || address?.trim()?.toUpperCase() === 'TBD'
}

export interface SubjectPropertyLocation {
  address?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  county?: string | null
}

export interface SelectedSubjectPropertyLocation {
  address?: string
  city?: string
  state?: string
  zip?: string
  county?: string
}

function normalizeSubjectPropertyLocation(
  location?: SubjectPropertyLocation
): SelectedSubjectPropertyLocation {
  return {
    address: location?.address || undefined,
    city: location?.city || undefined,
    state: location?.state || undefined,
    zip: location?.zip || undefined,
    county: location?.county || undefined,
  }
}

/**
 * Score a candidate by how many of the core subject-property location fields
 * (address, city, state, zip) are populated. County is intentionally excluded
 * because it is frequently missing on otherwise complete addresses.
 */
function getLocationCompletenessScore(
  location?: SubjectPropertyLocation
): number {
  if (!location) return 0
  let score = 0
  if (location.address) score++
  if (location.city) score++
  if (location.state) score++
  if (location.zip) score++
  return score
}

/**
 * Selects the subject-property location bundle to use, preferring the most
 * complete candidate (address + city + state + zip) whose street line is not
 * TBD. Candidates are returned wholesale (one source) to avoid mixing
 * incompatible address pieces across sources.
 *
 * Selection order:
 * 1. Among candidates with a non-TBD address, pick the one with the highest
 *    completeness score. Ties are broken by input order (loanImport first).
 * 2. If none have a non-TBD address, fall back to the first candidate that has
 *    any address populated.
 */
export function selectSubjectPropertyLocation({
  loanImportLocation,
  appRevisionLocation,
}: {
  loanImportLocation?: SubjectPropertyLocation
  appRevisionLocation?: SubjectPropertyLocation
}): SelectedSubjectPropertyLocation {
  const candidates = [loanImportLocation, appRevisionLocation]
  console.log(
    'selectSubjectPropertyLocation candidates',
    JSON.stringify(candidates, null, 2)
  )

  const nonTbdCandidates = candidates.filter(
    (location) => !isTBDAddress(location?.address)
  )

  if (nonTbdCandidates.length > 0) {
    const mostComplete = nonTbdCandidates.reduce((best, current) =>
      getLocationCompletenessScore(current) > getLocationCompletenessScore(best)
        ? current
        : best
    )
    return normalizeSubjectPropertyLocation(mostComplete)
  }

  return normalizeSubjectPropertyLocation(
    candidates.find((location) => Boolean(location?.address))
  )
}
